import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import _ from "lodash";
import {
  Combobox,
  LegacyStack,
  Listbox,
  BlockStack,
  Text,
  Button,
  Tag,
  Spinner,
} from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { API_URL } from "../../constants";
import { fetchFavoriteCategories } from "../../services/files-category.service";

interface FileCategorySingleSelectProps {
  value: string[];
  onChange: (value: string[]) => void;
  isRequired?: boolean;
  subject?: string;
}

interface CategoryOption {
  label: string;
  value: string;
}

function FileCategorySingleSelect({
  value,
  onChange,
  isRequired = false,
  subject = "customer",
}: FileCategorySingleSelectProps) {
  const { t } = useTranslation();
  const { companyId } = useParams<{ companyId: string }>();
  const [collection, setCollection] = useState<CategoryOption[]>([]);
  const [favoriteCategories, setFavoriteCategories] = useState<
    CategoryOption[]
  >([]);
  const [isLoading, setIsLoading] = useState<boolean>(() => Boolean(companyId));
  const [error, setError] = useState<string>("");
  const [inputValue, setInputValue] = useState<string>("");
  const [suggestion, setSuggestion] = useState<string>("");

  const selectedValue = value && value.length > 0 ? value[0] : "";
  const selectedLabel =
    collection.find((item) => item.value === selectedValue)?.label || "";

  useEffect(() => {
    if (isRequired && !selectedValue) {
      setError(t("field_is_required"));
    } else {
      setError("");
    }
  }, [selectedValue, isRequired, t]);

  const handleSelectChange = useCallback(
    (selected: string) => {
      if (selected) {
        onChange([selected]);
        // Clear input after selection
        setInputValue("");
        setSuggestion("");
      }
    },
    [onChange]
  );

  const handleFavoriteClick = useCallback(
    (categoryId: string) => {
      onChange([categoryId]);
      // Clear input after selection
      setInputValue("");
      setSuggestion("");
    },
    [onChange]
  );

  const handleRemoveSelected = useCallback(() => {
    onChange([""]);
    setInputValue("");
    setSuggestion("");
  }, [onChange]);

  function sortCategories(data: any[]): any[] {
    // Helper function to find children recursively with customized prefix
    const findChildren = (parentId: string | null, depth: number): any[] => {
      return data
        .filter((item) => item.parentCategory === parentId)
        .map((item) => {
          return {
            ...item,
            title: `${"﹘".repeat(depth)}⋗ ${item.title}`,
            children: findChildren(item.id, depth + 1),
          };
        });
    };

    // Get all parent categories (where parentCategory is null)
    const parents = data.filter((item) => item.parentCategory === null);

    // Build the sorted structure
    let sortedData: any[] = [];

    parents.forEach((parent) => {
      sortedData.push(parent);
      sortedData = sortedData.concat(findChildren(parent.id, 1));
    });

    // Flatten the result
    const flattenData = (items: any[]): any[] => {
      return items.reduce((acc: any[], item: any) => {
        acc.push(item);
        if (item.children) {
          acc = acc.concat(flattenData(item.children));
        }
        return acc;
      }, []);
    };

    return flattenData(sortedData);
  }

  useEffect(() => {
    async function fetchData() {
      try {
        setIsLoading(true);
        const collection = await fetch(companyId, subject);
        // Sort and print the result
        const sortedCategories = sortCategories(collection);
        const dataCollection = _.map(sortedCategories, (row) => ({
          label: row.title,
          value: row.id,
        }));
        dataCollection.unshift({ label: " - ", value: "" });
        setCollection(dataCollection);
        setIsLoading(false);
      } catch (e) {
        setIsLoading(false);
      }
    }
    if (companyId && subject) {
      fetchData();
    } else {
      setIsLoading(false);
    }
  }, [companyId, subject]);

  useEffect(() => {
    async function fetchFavorites() {
      try {
        const favorites = await fetchFavoriteCategories(companyId, subject);
        if (favorites && favorites.length > 0) {
          // Don't apply sortCategories for favorites - display them as-is
          const favoriteCollection = _.map(favorites, (row) => ({
            label: row.title,
            value: row.id,
          }));
          setFavoriteCategories(favoriteCollection);
        } else {
          setFavoriteCategories([]);
        }
      } catch (e) {
        setFavoriteCategories([]);
      }
    }
    if (companyId && subject) {
      fetchFavorites();
    }
  }, [companyId, subject]);

  // Filter options based on inputValue for search functionality
  const filteredOptions = inputValue
    ? collection.filter((option) =>
        option.label.toLowerCase().includes(inputValue.toLowerCase())
      )
    : collection;

  const optionsMarkup = filteredOptions.map((option, index) => {
    const isSelected = selectedValue === option.value;
    return (
      <Listbox.Option
        key={index}
        value={option.value}
        selected={isSelected}
        accessibilityLabel={option.label}
      >
        <Listbox.TextOption selected={isSelected}>
          {option.label}
        </Listbox.TextOption>
      </Listbox.Option>
    );
  });

  if (isLoading) {
    return (
      <div className="file-category-single-select">
        <BlockStack gap="200">
          <Text variant="bodySm" as="span" tone="subdued">
            {t("category")}
          </Text>
          <Spinner size="small" accessibilityLabel={t("category")} />
        </BlockStack>
      </div>
    );
  }

  return (
    <div className="file-category-single-select">
      {favoriteCategories.length > 0 && (
        <BlockStack gap="300">
          <Text variant="bodyMd" as="p" fontWeight="semibold">
            {t("favorite_categories")} ({favoriteCategories.length})
          </Text>
          <div
            style={{
              maxHeight: "200px",
              overflowY: "auto",
              border: "1px solid #e1e3e5",
              borderRadius: "6px",
              padding: "12px",
              backgroundColor: "#fafbfb",
            }}
          >
            <LegacyStack spacing="tight" wrap>
              {favoriteCategories.map((favorite) => {
                const isSelected = selectedValue === favorite.value;
                return (
                  <Button
                    key={favorite.value}
                    size="micro"
                    variant={isSelected ? "primary" : "secondary"}
                    onClick={() => handleFavoriteClick(favorite.value)}
                  >
                    {favorite.label}
                  </Button>
                );
              })}
            </LegacyStack>
          </div>
        </BlockStack>
      )}
      <Combobox
        activator={
          <Combobox.TextField
            label={t("category")}
            autoComplete="no"
            value={inputValue}
            placeholder={t("search_tags")}
            suggestion={suggestion}
            onChange={setInputValue}
            requiredIndicator={isRequired}
            error={error}
            verticalContent={
              selectedLabel ? (
                <LegacyStack spacing="extraTight">
                  <Tag onRemove={handleRemoveSelected}>{selectedLabel}</Tag>
                </LegacyStack>
              ) : null
            }
          />
        }
      >
        <Listbox onSelect={handleSelectChange}>{optionsMarkup}</Listbox>
      </Combobox>
    </div>
  );
}

export default FileCategorySingleSelect;

function fetch(companyId: string | undefined, subject: string) {
  if (!companyId) {
    return Promise.resolve([]);
  }
  const url = `${API_URL}/${companyId}/files/categories-fetch/${subject}`;
  return axios.get(url).then((response) => response.data);
}
